import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { SESSION_CONST } from 'src/app/core/constants/common.constant';
import { InactivityStateService } from 'src/app/services/inactivity/inactivity-state.service';
import { InactivityService } from 'src/app/services/inactivity/inactivity.service';

@Component({
  selector: 'app-inactivity-warning',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './inactivity-warning.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InactivityWarningComponent {
  readonly SESSION_CONST = SESSION_CONST;
  readonly countdown = signal(0);
  readonly isWarning = computed(() => this.inactivityState.state() === 'warning');

  private readonly inactivityState = inject(InactivityStateService);
  private readonly inactivityService = inject(InactivityService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.inactivityState.state$
      .pipe(
        switchMap((state) => {
          if (state !== 'warning') {
            this.countdown.set(0);
            return EMPTY;
          }

          this.countdown.set(this.getRemainingSeconds());
          return interval(1000).pipe(
            takeWhile((tick) => this.inactivityState.getValue() === 'warning' && tick >= 0),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.countdown.set(this.getRemainingSeconds());
      });
  }

  onContinue(): void {
    this.inactivityService.continueSession();
  }

  onLogout(): void {
    this.inactivityService.logoutFromWarning();
  }

  private getRemainingSeconds(): number {
    return Math.max(0, Math.ceil(this.inactivityService.getRemainingLogoutMs() / 1000));
  }
}
